import { useMutation, useQueries, useQueryClient } from '@tanstack/react-query';
import type { TFunction } from 'i18next';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { reportRegistrationApi } from '@/domains/traffic-monitoring/apis/report-registration';
import type {
  LocationRequestBody,
  ReportDecisionRequestBody,
  ReportMediaItem,
  ReportRegistrationDetailData,
  ReportRegistrationHeaderData,
  ReportRegistrationStatus,
} from '@/domains/traffic-monitoring/types/report-registration.types';

/**
 * Wires ReportRegistration.tsx (PC0202002) to api-dsh:
 *  - GET /report/{report_id} (registration form fields)
 *  - GET /report/{report_id}/media (image gallery + voice recordings)
 *  - POST /report/{report_id}/decision (register/dispatch or reject)
 *  - POST /report/{report_id}/location-request (WhatsApp the reporter a location link)
 */

// Backend-computed (get_report_detail_by_id.py reuses get_reports_today.py's
// exact event-type derivation) - this screen's badge always matches what the
// PSC dashboard card shows for the same report, including downstream stages
// (dispatched/arrived/.../completed) that a plain incoming/registered/rejected
// read would miss entirely.
const deriveStatus = (report: ReportRegistrationHeaderData | null): ReportRegistrationStatus =>
  report?.event_type ?? 'incoming';

const voiceNoteTitle = (item: ReportMediaItem, index: number, t: TFunction): string => {
  const filename = item.media_filepath?.split('/').pop();
  return filename || t('report>timeline>recording', { number: index + 1 });
};

export const useReportRegistration = (reportId?: number) => {
  const queryClient = useQueryClient();
  const enabled = reportId != null;
  // Recording fallback titles are built here; the language also keys the detail
  // query because the backend localizes its code labels per language_preference.
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  const { report, images, incomingCalls, voiceNotes, isLoading, isRefreshing, refetch } =
    useQueries({
      queries: [
        {
          queryKey: ['reportRegistrationDetail', reportId, lang],
          queryFn: async () => {
            const res = await reportRegistrationApi.getReportDetail(reportId as number);
            return (res?.data as ReportRegistrationDetailData | null)?.report ?? null;
          },
          enabled,
          refetchInterval: 2000,
        },
        {
          queryKey: ['reportRegistrationMedia', reportId],
          queryFn: async () => {
            const res = await reportRegistrationApi.getReportMedia(reportId as number);
            return res?.data?.media_list ?? [];
          },
          enabled,
          refetchInterval: 2000,
        },
      ],
      combine: ([detailQuery, mediaQuery]) => {
        const mediaList = (mediaQuery.data as ReportMediaItem[]) || [];
        return {
          report: (detailQuery.data as ReportRegistrationHeaderData | null) ?? null,
          images: mediaList
            .filter(
              (m) =>
                (m.media_type_id === 'MEDIA_TP_IMG' || m.media_type_id === 'MEDIA_TP_VID') && m.link
            )
            .map((m) => ({
              url: m.link as string,
              type: (m.media_type_id === 'MEDIA_TP_VID' ? 'video' : 'image') as 'video' | 'image',
            })),
          // MEDIA_TP_REC covers both call recordings and voice notes;
          // is_call_recording (resolved server-side via the parent chat_history
          // row's messages_type) tells them apart.
          incomingCalls: mediaList
            .filter((m) => m.media_type_id === 'MEDIA_TP_REC' && m.is_call_recording && m.link)
            .map((m, index) => ({
              id: m.media_files_id,
              title: voiceNoteTitle(m, index, t),
              audioUrl: m.link as string,
            })),
          voiceNotes: mediaList
            .filter((m) => m.media_type_id === 'MEDIA_TP_REC' && !m.is_call_recording && m.link)
            .map((m, index) => ({
              id: m.media_files_id,
              title: voiceNoteTitle(m, index, t),
              audioUrl: m.link as string,
            })),
          isLoading: detailQuery.isLoading || mediaQuery.isLoading,
          // isLoading is only true on the very first fetch (no cached data yet) -
          // a manual refetch() of already-loaded data only flips isFetching, so the
          // refresh button needs this to show it did something (same convention as
          // useAmbulanceDispatch.ts's isRefreshing).
          isRefreshing: detailQuery.isFetching || mediaQuery.isFetching,
          refetch: () => Promise.all([detailQuery.refetch(), mediaQuery.refetch()]),
        };
      },
    });

  const status = deriveStatus(report);

  const decisionMutation = useMutation({
    mutationFn: (body: ReportDecisionRequestBody) =>
      reportRegistrationApi.postReportDecision(reportId as number, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reportRegistrationDetail', reportId] });
    },
  });

  const locationRequestMutation = useMutation({
    mutationFn: (body: LocationRequestBody = {}) =>
      reportRegistrationApi.postLocationRequest(reportId as number, body),
  });

  // Lets a live MQTT chat message (see useReportChat) prompt this panel to
  // pick up a newly-arrived attachment or a WhatsApp-shared location without
  // waiting for the next natural refetch - a location message updates
  // reports.latitude/longitude server-side (handle_incoming_message.py) but
  // this REST-only query has no live-update path of its own.
  const refetchOnLiveMessage = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['reportRegistrationMedia', reportId] });
    queryClient.invalidateQueries({ queryKey: ['reportRegistrationDetail', reportId] });
  }, [queryClient, reportId]);

  return {
    report,
    status,
    images,
    incomingCalls,
    voiceNotes,
    isLoading,
    isRefreshing,
    refetch,
    decide: decisionMutation.mutate,
    isDeciding: decisionMutation.isPending,
    requestLocation: locationRequestMutation.mutate,
    isRequestingLocation: locationRequestMutation.isPending,
    refetchOnLiveMessage,
  };
};
